import * as Separator from '@radix-ui/react-separator'
import { CirclePlus } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import pic1 from '@/assets/images/guardian/pic1.webp'
import { Button } from '@/components/button'
import { MonetizationIcon } from '@/components/icons/monetization'
import { NaturePeopleIcon } from '@/components/icons/nature-people'
import { RealEstateIcon } from '@/components/icons/real-estate'
import { NewCardProperties } from '@/components/property/newCard-property'
import { Skeleton } from '@/components/skeleton'
import { TitleSeparator } from '@/components/title-separator'
import { useAuth } from '@/contexts'
import type { Property } from '@/models/property'
import api from '@/services/api'

import { GuardiansTestimonials } from '../guardians/guardians-testimonials'
import { Header } from './header'
import { Label } from './label'

export function BeGuardian() {
  const { signed } = useAuth()
  const navigate = useNavigate()
  const [limit, setLimit] = useState<number>(4)
  const [loading, setLoading] = useState<boolean>(true)
  const [properties, setProperties] = useState<Property[] | []>([])
  const [totalProperties, setTotalProperties] = useState<number>(0)

  const gridConservation = [
    {
      title: 'Por que conservar é vantajoso',
      text: 'A conservação ambiental, portanto, é um investimento no futuro da humanidade, assegurando que as gerações futuras possam desfrutar de um planeta saudável e sustentável.',
    },
    {
      title: 'Benefício de conservar',
      text: 'A preservação ambiental é benéfica por várias razões, abrangendo aspectos ecológicos, econômicos, sociais e de saúde.',
    },
    {
      title: 'Como lucrar conservando',
      text: 'A 6BIOS viabiliza a remuneração da conservação ambiental através dos Pagamentos por serviços ambientais e comercialização de créditos de carbono.',
    },
  ]

  useEffect(() => {
    const abortController = new AbortController()
    const signal = abortController.signal

    async function getLastProperties() {
      await api
        .get(`/last/property/${limit}`, { signal })
        .then((response) => {
          setProperties(response.data.properties)
          setTotalProperties(response.data.total)
        })
        .catch((error) => toast.error(error.response.data.message))
        .finally(() => setLoading(false))
    }
    getLastProperties()
    return () => {
      abortController.abort()
    }
  }, [limit])

  function loadMoreProjects() {
    if (properties.length === totalProperties || loading) return
    setLimit((prevLimit) => prevLimit + 4)
  }

  return (
    <div className="content-container flex-1">
      <div className="space-y-20">
        <Header />

        <div className="2xl:h-banner grid lg:h-120 xl:grid-cols-2">
          <div className="bg-bios-green-3000 order-2 flex flex-col gap-6 rounded-b-2xl px-8 py-6 text-center md:order-1 md:rounded-l-2xl md:rounded-br-none md:text-start lg:h-full xl:px-24 xl:py-20">
            <h1 className="card-title text-shadow-title text-bios-primary text-2xl md:text-4xl 2xl:text-6xl">
              Preservar é mais vantajoso do que explorar
            </h1>

            <p className="flex-1 text-lg text-white 2xl:text-xl">
              Preservar o meio ambiente oferece uma série de vantagens que
              beneficiam não apenas a natureza, mas também a sociedade como um
              todo, além de ser um excelente negócio.
            </p>

            {!signed && (
              <Button
                onClick={() => navigate('/register/preserver')}
                variant="outline"
                className="font-normal text-white uppercase"
              >
                Quero ser um guardião ambiental - Conservador
              </Button>
            )}
          </div>
          <div className="order-1 w-full overflow-hidden select-none md:order-2 md:h-40 lg:h-full">
            <img
              src={pic1}
              alt=""
              className="size-full rounded-t-2xl object-cover md:rounded-l-none md:rounded-r-xl"
            />
          </div>
        </div>

        <div className="space-y-10">
          <div className="overflow-x-auto">
            <div className="grid w-[230vw] grid-cols-3 gap-5 md:mx-12 md:mb-0 md:w-auto 2xl:mx-16">
              {gridConservation.map(({ title, text }) => (
                <div key={title} className="flex flex-col gap-6">
                  <div className="mx-auto flex">
                    <Label text={title} />
                  </div>
                  <div className="md:px-12 2xl:px-16">
                    <p className="text-center text-lg 2xl:text-xl">{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="border-bios-primary rounded-2xl border bg-white py-10">
            <h1 className="text-bios-title text-center text-2xl font-bold md:text-3xl 2xl:text-5xl">
              Como se tornar um guardião ambiental preservador?
            </h1>

            <div className="mx-5 overflow-x-auto md:mx-0">
              <div className="text-bios-primary mx-24 my-8 flex w-[160vw] items-center gap-56 md:mx-0 md:w-auto md:gap-0 md:px-52 2xl:px-72">
                <NaturePeopleIcon className="size-24" />

                <Separator.Root className="bg-bios-primary mx-16 hidden h-px w-1/3 md:block" />

                <RealEstateIcon className="size-24" />

                <Separator.Root className="bg-bios-primary mx-16 hidden h-px w-1/3 md:block" />

                <MonetizationIcon className="size-24" />
              </div>
              <div className="flex w-[200vw] items-center text-zinc-800 *:mx-5 *:text-center *:text-lg md:w-auto md:px-16 md:*:mx-12 xl:*:mx-6 2xl:*:mx-16 2xl:*:text-xl">
                <p>
                  Você precisa ser pessoa física ou jurídica com posse
                  comprovada de terras em situação regular ou projetos
                  reconhecidos de conservação de biomas brasileiros.
                </p>
                <p>
                  Cadastre suas propriedades na plataforma 6BIOS com imagens,
                  contando a história local e aguarda a aprovação.
                </p>

                <p>
                  Depois de aprovado, a 6BIOS divulgará seu trabalho de
                  preservação ou projeto de regeneração, buscando atrair a
                  atenção de investidores para sua propriedade.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-10">
          <GuardiansTestimonials title="Saiba o que outros guardiões pensam" />

          <div className="space-y-6">
            <h2 className="text-center text-2xl font-bold text-zinc-700 2xl:text-3xl">
              Conectamos de forma segura, transparente e auditada Guardiões
              Patrocinadores
              <br />
              <span className="text-xl font-normal">
                de todas as partes do mundo com conservadores dos biomas
                brasileiros
              </span>
            </h2>

            {!signed && (
              <div className="flex justify-center">
                <Button
                  onClick={() => navigate('/register')}
                  variant="primary"
                  className="text-white uppercase"
                >
                  Quero ser um guardião ambiental
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="mb-10 flex flex-col gap-5">
          <TitleSeparator title="Novos projetos" />
          <div className="grid h-96 flex-1 grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4">
            {properties.length <= 0 &&
              Array(4)
                .fill(0)
                .map((_, index) => (
                  <Skeleton key={index} className="h-96 w-full rounded-2xl" />
                ))}
          </div>

          {properties && properties.length > 0 && (
            <NewCardProperties properties={properties} />
          )}
          <div className="flex flex-col items-center justify-center gap-5 sm:flex-row">
            <Button
              className="text-monochromatic-400 flex items-center gap-2 font-semibold uppercase disabled:border-none disabled:bg-transparent"
              variant="ghost"
              disabled={properties.length === totalProperties || loading}
              onClick={loadMoreProjects}
            >
              <CirclePlus size={20} />
              Veja mais projetos
            </Button>

            {!signed && (
              <Button
                onClick={() => navigate('/register')}
                className="uppercase"
              >
                Patrocine uma área
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
